import { createSelector } from 'reselect'
import AuthStatus from '../constants/auth-status'
import InitStatus from '../constants/init-status'
import VerifyPushStatus from '../constants/verify-push-status'
import FetchAuthenticatorStatus from '../constants/fetch-authenticators'
import QRCodeStatus from '../constants/fetch-qr-code-status'
import resendMessage from '../constants/resend-message'
import FetchAccountRecovery from '../constants/fetch-account-recovery'
import { ACTIVATE_AUTHENTICATOR_CODE,
    ACTIVATE_AUTHENTICATOR_CODE_ERROR,
    ACTIVATE_AUTHENTICATOR_CODE_NEXT_STEP,
    ACTIVATE_AUTHENTICATOR_CODE_SUCCESS,
    FORGOTPWD,
    FORGOTPWD_SUCCESS,
    FORGOTPWD_ERROR,
    INIT_AUTHENTICATORS,
    INIT_AUTHENTICATORS_ERROR,
    INIT_AUTHENTICATORS_SUCCESS,
    INIT_ENROLL_AUTH_FACTOR,
    INIT_ENROLL_AUTH_FACTOR_ERROR,
    INIT_ENROLL_AUTH_FACTOR_SUCCESS,
    ON_AUTHENTICATE,
    ON_AUTHENTICATE_ERROR,
    ON_AUTHENTICATE_SUCCESS,
    ON_BACK_AUTHENTICATORS,
    ON_BACK_OPTIONAL_AUTHENTICATORS,
    ON_BACK_FALLBACK_ENFORCE_MFA,
    ON_BACK_FALLBACK_ENFORCE_MFA_TWO_STEPS,
    ON_ENROLL_MFA,
    ON_ENROLL_MFA_ERROR,
    ON_ENROLL_MFA_SUCCESS,
    ON_LOGIN_APP,
    ON_LOGIN_APP_ERROR,
    ON_LOGIN_APP_SUCCESS,
    ON_RESEND_AUTH_CHALLENGE,
    ON_RESEND_AUTH_CHALLENGE_ERROR,
    ON_RESEND_AUTH_CHALLENGE_SUCCESS,
    ON_RESEND_CHALLENGE,
    ON_RESEND_CHALLENGE_ERROR,
    ON_RESEND_CHALLENGE_SUCCESS,
    ON_VERIFY_ACCOUNT_RECOVERY,
    ON_VERIFY_ACCOUNT_RECOVERY_ERROR,
    ON_VERIFY_ACCOUNT_RECOVERY_SUCCESS,
    ON_VERIFY_ACCOUNT_RECOVERY_NEXT_STEP,
    ON_VERIFY_AUTH_ACCOUNT_RECOVERY,
    ON_VERIFY_AUTH_ACCOUNT_RECOVERY_ERROR,
    ON_VERIFY_AUTH_ACCOUNT_RECOVERY_SUCCESS,
    QR_CODE_CANNOT_SCAN,
    QR_CODE_CANNOT_SCAN_NEXT,
    QR_CODE_NEXT,
    SET_AUTHENTICATOR,
    SET_DEVICE_TYPE,
    SET_OPTIONAL_AUTHENTICATOR,
    SKIP_ENROLLMENT,
    SKIP_ENROLLMENT_ERROR,
    SKIP_ENROLLMENT_SUCCESS,
    VALIDATE_SESSION_ERROR,
    VALIDATE_SESSION_SUCCESS,
    VERIFY_AUTHENTICATOR_CODE,
    VERIFY_AUTHENTICATOR_CODE_ERROR,
    VERIFY_AUTHENTICATOR_CODE_SUCCESS,
    VERIFY_AUTHENTICATOR_CODE_NEXT_STEP,
    RESET_AUTH_VERIFIED,
    VERIFY_PUSH,
    VERIFY_PUSH_ERROR,
    VERIFY_PUSH_SUCCESS,
    GET_BANNER_INFO_SUCCESS,
    CLOSE_BANNER,
    ON_INIT,
    ON_INIT_SUCCESS,
    SESSION_TIMEOUT,
    GET_ACCOUNT_RECOVERY,
    GET_ACCOUNT_RECOVERY_SUCCESS,
    GET_ACCOUNT_RECOVERY_ERROR,
    ENROLL_MFA_INIT,
    VALIDATE_PHONE_AT_LOGIN,
    VALIDATE_PHONE_AT_LOGIN_ERROR,
    VALIDATE_PHONE_AT_LOGIN_SUCCESS,
    ENROLL_MFA_INIT_SUCCESS,
    ENROLL_MFA_INIT_FAILURE, 
    ON_SEND_CHALLENGE,
    ON_SEND_CHALLENGE_ERROR,
    ON_SEND_CHALLENGE_SUCCESS,
    MAGIC_LINK_LOGIN,
    MAGIC_LINK_LOGIN_SUCCESS,
    MAGIC_LINK_LOGIN_ERROR,
    MAGIC_LINK_VERIFY,
    MAGIC_LINK_VERIFY_SUCCESS,
    MAGIC_LINK_VERIFY_ERROR } from '../constants/login'


export const initialState = {
    accountRecoveryData: [],
    accountRecoveryStatus: FetchAccountRecovery.DEFAULT,
    activeSession: false,
    authData: null,
    bannerInfo: {
        display: false,
        message: '',
        messageFr: '',
        severity: ''
    },
    data: {},
    errorAuth: null,
    errorMagicLinkLogin: null,
    errorSession: null,
    forgotPwdProgress: false,
    initStatus: InitStatus.PENDING,
    magicLinkLoginMsg: '',
    isVerified: false,
    mfaRequired: false,
    resendSuccess: null,
    resendSuccessMsg: resendMessage.DEFAULT,
    sessionTimeout: false,
    step: 0,
    validatePhoneAtLogin: false
}

// Selectors
const selectRequestData = state => state.login

const selectStateFor = key => createSelector(
    selectRequestData,
    (request = {}) => request[key]
)

export const selectBannerInfo = selectStateFor('bannerInfo')
export const selectActiveLoginSession = selectStateFor('activeSession')
export const selectAuthData = selectStateFor('authData')
export const selectAccountRecoveryData = selectStateFor('accountRecoveryData')
export const selectAccountRecoveryStatus = selectStateFor('accountRecoveryStatus')
export const selectAuthenticatorData = selectStateFor('authenticatorData')
export const selectAuthenticatorStatus = selectStateFor('authenticatorStatus')
export const selectAuthStatus = selectStateFor('authStatus')
export const selectInitStatus = selectStateFor('initStatus')
export const selectError = selectStateFor('error')
export const selectErrorAuth = selectStateFor('errorAuth')
export const selectErrorForgotPwd = selectStateFor('forgotPwdErrorMsg')
export const selectErrorSession = selectStateFor('errorSession')
export const selectForgotPwdSuccessMsg = selectStateFor('forgotPwdSuccessMsg')
export const selectForgotPwdProgess = selectStateFor('forgotPwdProgress')
export const selectIsVerified = selectStateFor('isVerified')
export const selectAuthVerified = selectStateFor('authVerified')
export const selectQRCodeData = selectStateFor('qrCodeData')
export const selectQRCodeStatus = selectStateFor('qrCodeStatus')
export const selectResendSuccess = selectStateFor('resendSuccess')
export const selectResendSuccessMsg = selectStateFor('resendSuccessMsg')
export const selectStep = selectStateFor('step')
export const selectMfaRequired = selectStateFor('mfaRequired')
export const selectVerifyPushStatus = selectStateFor('verifyPushStatus')
export const selectLoginSessionTimeout = selectStateFor('sessionTimeout')
export const selectValidatePhoneAtLogin = selectStateFor('validatePhoneAtLogin')
export const selectMagicLinkLoginMsg = selectStateFor('magicLinkLoginMsg')
export const selectMagicLinkVerificationStatus = selectStateFor('magicLinkVerifyStatus')
export const selectErrorMagicLinkLogin = selectStateFor('errorMagicLinkLogin')
export const selectErrorMagicLinkVerify = selectStateFor('errorMagicLinkVerify')


export default function login(state = initialState, action) {
    switch (action.type) {
        case GET_BANNER_INFO_SUCCESS:
            return {
                ...state,
                bannerInfo: action.payload
            }
        case CLOSE_BANNER:
            return {
                ...state,
                bannerInfo: {
                    ...state.bannerInfo,
                    display: false
                }
            }
        case ACTIVATE_AUTHENTICATOR_CODE:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                error: null
            }
        case ACTIVATE_AUTHENTICATOR_CODE_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                authVerified: false,
                error: action.payload
            }
        case ACTIVATE_AUTHENTICATOR_CODE_SUCCESS:
            return {
                ...state,
                authData: action.payload,
                authStatus: AuthStatus.SUCCESS,
                authVerified: true,
                error: null
            }
        case ACTIVATE_AUTHENTICATOR_CODE_NEXT_STEP:
            return {
                ...state,
                step: state.step - 4
            }

        case FORGOTPWD:
            return {
                ...state,
                forgotPwdErrorMsg: null,
                forgotPwdProgress: true,
                forgotPwdSuccessMsg: null
            }
        case FORGOTPWD_SUCCESS:
            return {
                ...state,
                forgotPwdErrorMsg: null,
                forgotPwdProgress: false,
                forgotPwdSuccessMsg: action.payload
            }
        case FORGOTPWD_ERROR:
            return {
                ...state,
                forgotPwdErrorMsg: action.payload,
                forgotPwdProgress: false,
                forgotPwdSuccessMsg: null
            }

        case INIT_AUTHENTICATORS:
            return {
                ...state,
                authenticatorStatus: FetchAuthenticatorStatus.PENDING,
                error: null
            }
        case INIT_AUTHENTICATORS_SUCCESS:
            return {
                ...state,
                authenticatorData: { ...action.payload.authenticators },
                authenticatorStatus: FetchAuthenticatorStatus.SUCCESS,
                error: null,
                mfaRequired: action.payload?.mfaRequired || false
            }
        case INIT_AUTHENTICATORS_ERROR:
            return {
                ...state,
                authenticatorStatus: FetchAuthenticatorStatus.ERROR,
                error: action.payload
            }
        case INIT_ENROLL_AUTH_FACTOR:
            return {
                ...state,
                error: null,
                qrCodeStatus: QRCodeStatus.PENDING
            }
        case INIT_ENROLL_AUTH_FACTOR_ERROR:
            return {
                ...state,
                error: action.payload,
                qrCodeStatus: QRCodeStatus.ERROR
            }
        case INIT_ENROLL_AUTH_FACTOR_SUCCESS:
            return {
                ...state,
                error: null,
                qrCodeData: action.payload,
                qrCodeStatus: QRCodeStatus.SUCCESS
            }
        case ON_AUTHENTICATE:
            return {
                ...state,
                authData: null,
                authStatus: AuthStatus.PENDING,
                errorAuth: null,
                forgotPwdErrorMsg: null,
                forgotPwdSuccessMsg: null,
                step: 0
            }
        case ON_AUTHENTICATE_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                errorAuth: action.payload
            }
        case ON_AUTHENTICATE_SUCCESS:
            return {
                ...state,
                activeSession: true,
                authData: { ...action.payload },
                authStatus: AuthStatus.SUCCESS,
                errorAuth: null
            }
        case ON_RESEND_AUTH_CHALLENGE:
            return {
                ...state,
                resendSuccess: true,
            }
        case ON_RESEND_CHALLENGE:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                error: null,
                resendSuccessMsg: resendMessage.DEFAULT
            }
        case ON_RESEND_AUTH_CHALLENGE_ERROR:
        case ON_RESEND_CHALLENGE_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload,
                resendSuccess: false,
                resendSuccessMsg: resendMessage.DEFAULT
            }
        case ON_RESEND_AUTH_CHALLENGE_SUCCESS:
        case ON_RESEND_CHALLENGE_SUCCESS:
            return {
                ...state,
                authStatus: AuthStatus.SUCCESS,
                error: null,
                resendSuccess: true,
                resendSuccessMsg: resendMessage.SUCCESS
            }
        case QR_CODE_NEXT:
            return {
                ...state,
                step: state.step + 2
            }
        case QR_CODE_CANNOT_SCAN:
            return {
                ...state,
                error: null,
                step: state.step + 1
            }
        case QR_CODE_CANNOT_SCAN_NEXT:
            return {
                ...state,
                error: null,
                step: state.step + 1
            }
        case SET_AUTHENTICATOR:
            return {
                ...state,
                step: state.step + 1
            }
        case SET_DEVICE_TYPE:
            return {
                ...state,
                step: state.step + 1
            }
        case SET_OPTIONAL_AUTHENTICATOR:
            return {
                ...state,
                step: 3
            }
        case ON_BACK_AUTHENTICATORS:
            return {
                ...state,
                step: 1
            }
        case ON_BACK_OPTIONAL_AUTHENTICATORS:
            return {
                ...state,
                step: 3
            }
        case ON_BACK_FALLBACK_ENFORCE_MFA:
            return {
                ...state,
                step: state.step - 1
            }
        case ON_BACK_FALLBACK_ENFORCE_MFA_TWO_STEPS:
            return {
                ...state,
                step: state.step - 2
            }
        case ON_ENROLL_MFA:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                error: null
            }
        case ON_ENROLL_MFA_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload
            }
        case ON_ENROLL_MFA_SUCCESS:
            return {
                ...state,
                authData: { ...action.payload },
                authStatus: AuthStatus.SUCCESS,
                error: null,
                step: state.step + 1
            }
        case ON_SEND_CHALLENGE:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                error: null
            }
        case ON_SEND_CHALLENGE_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload
            }
        case ON_SEND_CHALLENGE_SUCCESS:
            return {
                ...state,
                authStatus: AuthStatus.SUCCESS,
                error: null,
                step: state.step + 1
            }
        case ON_LOGIN_APP:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
            }
        case ON_LOGIN_APP_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload
            }
        case ON_LOGIN_APP_SUCCESS:
            return {
                ...state,
                authData: { ...action.payload },
                authStatus: AuthStatus.SUCCESS
            }
        case ON_VERIFY_ACCOUNT_RECOVERY:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                error: null
            }
        case ON_VERIFY_ACCOUNT_RECOVERY_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload,
                isVerified: false,
            }
        case ON_VERIFY_ACCOUNT_RECOVERY_SUCCESS:
            return {
                ...state,
                authData: { ...action.payload },
                authStatus: AuthStatus.SUCCESS,
                isVerified: true
            }
        case ON_VERIFY_ACCOUNT_RECOVERY_NEXT_STEP:
            return {
                ...state,
                step: state.step + 1
            }
        case ON_VERIFY_AUTH_ACCOUNT_RECOVERY:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                error: null
            }
        case ON_VERIFY_AUTH_ACCOUNT_RECOVERY_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload
            }
        case ON_VERIFY_AUTH_ACCOUNT_RECOVERY_SUCCESS:
            return {
                ...state,
                authData: { ...action.payload },
                authStatus: AuthStatus.SUCCESS,
                error: null,
                step: state.step + 1
            }
        case SKIP_ENROLLMENT:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                error: null
            }
        case SKIP_ENROLLMENT_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload
            }
        case SKIP_ENROLLMENT_SUCCESS:
            console.log('Reducer SKIP_ENROLLMENT_SUCCESS payload:', action.payload) // Debug log
            return {
                ...state,
                accountRecoveryData: [ ...(action.payload.accountRecovery || []) ],
                authData: { ...action.payload },
                authStatus: AuthStatus.SUCCESS,
                error: null,
                step: 1,
                validatePhoneAtLogin: action.payload.validatePhoneAtLogin,
            }
        case VALIDATE_SESSION_ERROR:
            return {
                ...state,
                errorSession: action.payload,
            }
        case VALIDATE_SESSION_SUCCESS:
            return {
                ...state,
                errorSession: null,
            }
        case VERIFY_AUTHENTICATOR_CODE:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                error: null
            }
        case VERIFY_AUTHENTICATOR_CODE_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                authVerified: false,
                error: action.payload
            }
        case VERIFY_AUTHENTICATOR_CODE_SUCCESS:
            return {
                ...state,
                authData: { ...action.payload },
                authStatus: AuthStatus.SUCCESS,
                authVerified: true,
                error: null
            }
        case VERIFY_AUTHENTICATOR_CODE_NEXT_STEP:
            return {
                ...state,
                step: state.step + 1
            }
        case RESET_AUTH_VERIFIED:
            return {
                ...state,
                authVerified: false
            }
        case ON_INIT:
            return {
                ...state,
                initStatus: InitStatus.PENDING,
            }
        case ON_INIT_SUCCESS:
            return {
                ...state,
                initStatus: InitStatus.SUCCESS,
            }
        case VERIFY_PUSH:
            return {
                ...state,
                error: null,
                resendSuccess: null,
                verifyPushStatus: VerifyPushStatus.PENDING
            }
        case VERIFY_PUSH_ERROR:
            return {
                ...state,
                error: action.payload,
                resendSuccess: null,
                verifyPushStatus: VerifyPushStatus.ERROR
            }
        case VERIFY_PUSH_SUCCESS:
            return {
                ...state,
                authData: { ...action.payload },
                error: null,
                resendSuccess: null,
                step: state.step + 1,
                verifyPushStatus: VerifyPushStatus.SUCCESS
            }
        case SESSION_TIMEOUT: {
            return {
                ...state,
                sessionTimeout: true
            }
        }
        case GET_ACCOUNT_RECOVERY:
            return {
                ...state,
                accountRecoveryData: [],
                accountRecoveryStatus: FetchAccountRecovery.PENDING,
            }
        case GET_ACCOUNT_RECOVERY_SUCCESS:
            return {
                ...state,
                accountRecoveryData: action.payload,
                accountRecoveryStatus: FetchAccountRecovery.SUCCESS,
            }
        case GET_ACCOUNT_RECOVERY_ERROR:
            return {
                ...state,
                accountRecoveryData: [],
                accountRecoveryStatus: FetchAccountRecovery.ERROR,
            }
        case ENROLL_MFA_INIT: {
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
            }
        }
        case ENROLL_MFA_INIT_SUCCESS: {
            return {
                ...state,
                authStatus: AuthStatus.SUCCESS,
                step: 1,
            }
        }
        case ENROLL_MFA_INIT_FAILURE: {
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload,
                step: 0,
            }
        }
        case VALIDATE_PHONE_AT_LOGIN:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
            }
        case VALIDATE_PHONE_AT_LOGIN_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                error: action.payload
            }
        case VALIDATE_PHONE_AT_LOGIN_SUCCESS:
            return {
                ...state,
                authData: { ...action.payload },
                authStatus: AuthStatus.SUCCESS,
                error: null,
                step: 2
            }
        case MAGIC_LINK_LOGIN:
            return {
                ...state,
                authStatus: AuthStatus.PENDING,
                errorMagicLinkLogin: null,
                magicLinkLoginMsg: '',
            }
        case MAGIC_LINK_LOGIN_SUCCESS:
            return {
                ...state,
                authStatus: AuthStatus.SUCCESS,
                errorMagicLinkLogin: null,
                magicLinkLoginMsg: action.payload,
            }
        case MAGIC_LINK_LOGIN_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                errorMagicLinkLogin:  action.payload,
                magicLinkLoginMsg: '',
            } 
        case MAGIC_LINK_VERIFY:
            return {
                ...state,
                magicLinkVerifyStatus: AuthStatus.PENDING,
            }
        case MAGIC_LINK_VERIFY_SUCCESS:
            return {
                ...state,
                activeSession: true,
                magicLinkVerifyStatus: AuthStatus.SUCCESS,
                errorMagicLinkVerify: null,
                authData: { ...action.payload }
            }
        case MAGIC_LINK_VERIFY_ERROR:
            return {
                ...state,
                magicLinkVerifyStatus: AuthStatus.ERROR,
                errorMagicLinkVerify: action.payload
            }
        case MAGIC_LINK_LOGIN_SUCCESS:
            return {
                ...state,
                authStatus: AuthStatus.SUCCESS,
                errorMagicLinkLogin: null,
                magicLinkLoginMsg: action.payload,
            }
        case MAGIC_LINK_LOGIN_ERROR:
            return {
                ...state,
                authStatus: AuthStatus.ERROR,
                errorMagicLinkLogin:  action.payload,
                magicLinkLoginMsg: '',
            }            
        default:
            return state
    }
}
